import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { contactMessage, useT } from '../i18n';
import { config } from '../config';
import { selectedLotStore, type LotSpec } from '../data/plots';
import { useNarrowViewport } from './useNarrowViewport';
import { WhatsappIcon } from './WhatsappIcon';

type LocateState = 'idle' | 'locating' | 'denied' | 'unavailable' | 'live';

/**
 * Minimal footer for low-data mode — shows Contact Us plus a small
 * "Use 3D" toggle so the user can opt back into the Photoreal experience
 * once they're on a faster network. Hidden whenever a lot is selected
 * (LotDetails takes over the footer slot, same as in CesiumViewer mode).
 */
export function LowDataFooter() {
  const { t, lang } = useT();
  const narrow = useNarrowViewport();
  const [selectedLot, setSelectedLot] = useState<LotSpec | null>(
    selectedLotStore.get(),
  );
  const [locateState, setLocateState] = useState<LocateState>('idle');
  const markerRef = useRef<L.CircleMarker | null>(null);
  const haloRef = useRef<L.Circle | null>(null);

  useEffect(() => {
    const unsub = selectedLotStore.subscribe(setSelectedLot);
    return () => {
      unsub();
    };
  }, []);

  const handleLocateMe = () => {
    const map = (window as unknown as { leafletMap?: L.Map }).leafletMap;
    if (!map) return;
    if (!('geolocation' in navigator)) {
      setLocateState('unavailable');
      return;
    }
    setLocateState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        if (markerRef.current) markerRef.current.remove();
        if (haloRef.current) haloRef.current.remove();
        haloRef.current = L.circle([latitude, longitude], {
          radius: Math.max(accuracy, 10),
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.15,
          weight: 1,
        }).addTo(map);
        markerRef.current = L.circleMarker([latitude, longitude], {
          radius: 7,
          color: '#fff',
          weight: 2,
          fillColor: '#3b82f6',
          fillOpacity: 1,
        }).addTo(map);
        map.setView(
          [latitude, longitude],
          Math.max(map.getZoom(), 16),
          { animate: true },
        );
        setLocateState('live');
      },
      (err) => {
        setLocateState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 30_000 },
    );
  };

  if (selectedLot) return null;

  const contactHref = `https://wa.me/${config.contact.whatsappNumber}?text=${encodeURIComponent(
    contactMessage(null, lang),
  )}`;

  return (
    <div
      id="manglillo-footer"
      style={{
        position: 'absolute',
        bottom: narrow ? 0 : 16,
        right: narrow ? 0 : 16,
        left: narrow ? 0 : 'auto',
        display: 'flex',
        gap: 6,
        padding: 6,
        background: 'rgba(0,0,0,0.62)',
        color: 'white',
        borderRadius: narrow ? 0 : 8,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 12,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 5,
        justifyContent: narrow ? 'center' : 'flex-start',
      }}
    >
      <button
        type="button"
        onClick={() => {
          const map = (window as unknown as {
            leafletMap?: L.Map & { setBearing?: (deg: number) => void };
          }).leafletMap;
          if (!map) return;
          const rv = config.lowDataResetView;
          map.flyTo([rv.latitude, rv.longitude], rv.zoom, { duration: 1.2 });
          map.setBearing?.(rv.bearing);
        }}
        style={btnStyle}
        title={t('reset_view_title')}
      >
        🔄 Reset
      </button>
      {/* "🌐 3D" switcher now lives in the PositionHUD menu. */}
      <button
        type="button"
        onClick={handleLocateMe}
        disabled={locateState === 'locating'}
        className={locateState === 'locating' ? 'manglillo-pulse' : undefined}
        style={{
          ...btnStyle,
          color:
            locateState === 'denied' || locateState === 'unavailable'
              ? '#ffb84d'
              : 'white',
        }}
        title={
          locateState === 'denied'
            ? lang === 'es' ? 'Permiso de GPS denegado' : 'GPS permission denied'
            : locateState === 'unavailable'
              ? lang === 'es' ? 'GPS no disponible' : 'GPS not available'
              : t('locate_me')
        }
      >
        {locateState === 'locating'
          ? `📡 ${t('locating')}`
          : `📍 ${t('locate_me')}`}
      </button>
      <a
        href={contactHref}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          padding: '6px 12px',
          borderRadius: 5,
          border: '1px solid #1da851',
          background: '#25D366',
          color: '#fff',
          fontFamily: 'inherit',
          fontSize: 12,
          fontWeight: 600,
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
        }}
      >
        <WhatsappIcon />
        {t('contact_us')}
      </a>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.2)',
  background: 'rgba(255,255,255,0.06)',
  color: 'white',
  fontFamily: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
